import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { colors, typography } from '../../../styles/theme';
import { withAlpha } from '../../utils/sharedBranding';
import { buildLotShapeThumbnail } from '../../utils/lotShapeThumbnail';

/**
 * Miniatura decorativa del polígono del lote. El slot de portada fija el
 * recorte; acá solo el plano (grilla + silueta). Los vecinos van atrás,
 * más tenues; el lote principal lleva el color de marca.
 *
 * Con vecinos el marco es un recorte de plano y llena la card (`slice`), así el
 * borde corta parejo. Sin vecinos la silueta tiene que entrar entera (`meet`).
 */
export default function LotShapeThumbnail({ boundary, neighborBoundaries, accentColor }) {
  const layout = useMemo(
    () => buildLotShapeThumbnail(boundary, neighborBoundaries),
    [boundary, neighborBoundaries],
  );
  const stroke = accentColor || colors.primary;
  const fill = withAlpha(stroke, 0.34);
  const gridLine = withAlpha(stroke, 0.16);
  const wash = withAlpha(stroke, 0.14);
  const neighborFill = 'rgba(255,255,255,0.05)';
  const neighborStroke = 'rgba(255,255,255,0.28)';

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        bgcolor: 'rgba(0,0,0,0.28)',
        backgroundImage: `
          radial-gradient(ellipse at 50% 42%, ${wash} 0%, transparent 68%),
          linear-gradient(${gridLine} 1px, transparent 1px),
          linear-gradient(90deg, ${gridLine} 1px, transparent 1px)
        `,
        backgroundSize: '100% 100%, 14px 14px, 14px 14px',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
      }}
    >
      {layout ? (
        <svg
          viewBox={layout.viewBox}
          preserveAspectRatio={layout.hasContext ? 'xMidYMid slice' : 'xMidYMid meet'}
          width="100%"
          height="100%"
          style={{ display: 'block' }}
        >
          {(layout.neighborPaths || []).map((d, index) => (
            <path
              key={`neighbor-${index}`}
              d={d}
              fill={neighborFill}
              stroke={neighborStroke}
              strokeWidth={layout.neighborStrokeWidth}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
          <path
            d={layout.path}
            fill={fill}
            stroke={stroke}
            strokeWidth={layout.strokeWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      ) : null}
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 5,
          right: 7,
          fontFamily: typography.fontFamily.mono,
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: withAlpha(stroke, 0.55),
          lineHeight: 1,
        }}
      >
        N
      </Box>
    </Box>
  );
}
